<template>
  <div class="bg-white h-auto px-5 rounded-lg">
    <div
      class="flex flex-col lg:flex-row md:items-center lg:gap-2 border-b-[1.5px] border-primary-purple-100 py-5"
    >
      <div class="flex items-center w-full justify-between">
        <Heading heading="Campus Attendance" heading-size="text-[18px]" heading-padding="py-0" />
        <div
          v-permission="CAMPUS_ATTENDANCE_FILTERS_PERMISSION"
          class="xl:h-11 md:h-10 h-9 flex items-center cursor-pointer justify-center gap-2 bg-white border border-primary-gray-300 rounded-lg px-2"
        >
          <icon icon="filter" height="20" width="20" color="primary-gray-700" />
          <span class="font-inter font-semibold text-primary-gray-700 text-sm hidden sm:block">
            Filters
          </span>
        </div>
      </div>
      <div
        class="w-full lg:w-fit flex flex-wrap-reverse sm:flex-nowrap gap-2 items-end justify-end"
      >
        <UiFilterBox
          v-model="selectedCampus"
          :options="campusList"
          :is-switch-box="true"
          :is-disabled="isCampusDisabled"
          label="title"
          reduce="id"
          class=""
        />
        <CalendarActionsWrapper
          :is-future-date-disabled="true"
          :date-format="DATE_TIME_CONSTANT.SHORT_MONTH_FULL_YEAR"
          :calendar-date="selectedDate"
          filter-view="Month"
          custom-class="right-0 before:right-12"
          @selectDate="onDateChange"
        />
      </div>
    </div>
    <div class="overflow-auto">
      <Loader v-if="isLoading" class="mt-20 mb-40" :content="true" />
      <NoRecordFound v-else-if="isEmptyGraph" />
      <GraphView v-else :chart-data="chartData" class="min-w-7" />
    </div>
    <div class="pb-6 flex mt-2">
      <UiStatsCountDisplay title="Total Students:" :value="totalStudents" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, reactive } from 'vue'
import UiStatsCountDisplay from '@/src/components/UiElements/UiStatsCountDisplay.vue'
import UiFilterBox from '@/src/components/UiElements/UiFilterBox.vue'
import CalendarActionsWrapper from '@components/Calendar/CalendarActionsWrapper.vue'
import Heading from '@/src/components/Heading.vue'
import GraphView from '@src/router/views/graph/graphView.vue'
import icon from '@src/components/icons/icon.vue'
import Loader from '@src/components/BaseComponent/Loader.vue'
import { useStore } from 'vuex'
import { arrayRange } from '@utils/generalUtil'
import { getDaysInMonth, getDatesForMonth } from '@src/utils/moment.util.js'
import { buildWhereQuery } from '@/src/utils/filters'
import FILTER_KEYS from '@src/constants/filter-constants.js'
import NoRecordFound from '@/src/components/BaseComponent/NoRecordFound.vue'
import DATE_TIME_CONSTANT from '@src/constants/date-time-constants.js'
import { ENV } from '@src/constants/general-constants.js'

// Vuex store
const store = useStore()

// State variables
const isLoading = ref(false)
const selectedDate = ref(new Date())
const selectedCampus = ref('')
const checkInData = ref([])
const checkOutData = ref([])
const totalStudents = ref('')
const daysInCurrentMonth = ref(30)
var isSuccessCampusStats = ref(false)
// Computed properties
const currentCampusScope = computed(() => store.state.layout.currentCampusScope)
const campusList = computed(() => store.state.layout.campuses)
const isCampusDisabled = computed(() => !!currentCampusScope.value)
const isEmptyGraph = computed(() => {
  return chartData.data?.datasets?.every(({ data }) => !data?.length || data.every((item) => !item))
})
// Chart data reactive object
const CAMPUS_ATTENDANCE_FILTERS_PERMISSION = {
  env: [ENV.DEV],
}
const chartData = reactive({
  tooltipHeader: [],
  type: 'bar',
  data: {
    labels: arrayRange(1, 30),
    datasets: [
      {
        label: 'Campus In',
        backgroundColor: '#7F56D9',
        data: checkInData.value,
      },
      {
        label: 'Campus Out',
        backgroundColor: '#D6BBFB',
        data: checkOutData.value,
      },
    ],
  },
  options: {
    responsive: true,
    pointRadius: 4,
    maintainAspectRatio: false,
    aspectRatio: 100 / 80,
    borderRadius: { topLeft: 5, topRight: 5 },
    scales: {
      x: { grid: { display: false } },
      y: { ticks: { stepSize: 1 } },
    },
    plugins: {
      tooltip: { backgroundColor: 'white', titleColor: 'black', bodyColor: '#667085' },
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          boxWidth: 8,
          boxHeight: 8,
          usePointStyle: true,
          padding: 13,
          font: { size: 14 },
        },
      },
    },
    interaction: {
      mode: 'index',
    },

    maxBarThickness: 8,
  },
})

// Fetch campus attendance stats
const fetchCampusAttendanceStats = async () => {
  if (currentCampusScope.value) {
    selectedCampus.value = currentCampusScope.value.id
  }
  if (selectedCampus.value) {
    const month = selectedDate.value.getMonth() + 1
    const year = selectedDate.value.getFullYear()

    isLoading.value = true
    const payload = {
      month,
      year,
      ...(currentCampusScope.value
        ? {}
        : { $where: { ...buildWhereQuery(FILTER_KEYS.EQUAL, 'campus_id', selectedCampus.value) } }),
    }

    try {
      const response = await CampusAttendanceStats(payload)
      checkInData.value = response.stats_by_month.map((entry) => entry.total_checked_in)
      checkOutData.value = response.stats_by_month.map((entry) => entry.total_checked_out)
      totalStudents.value = response.students_strength
    } finally {
      isLoading.value = false
    }
  }
}
const setLastAttendanceCampus = async () => {
  if (currentCampusScope.value) {
    selectedCampus.value = currentCampusScope.value.id
  } else {
    isLoading.value = true
    try {
      const response = await lastMarkedCampusAttendance()
      if (response) {
        selectedCampus.value = response.id
      }
      await fetchCampusAttendanceStats()
    } finally {
      isLoading.value = false
    }
  }
}
const onDateChange = (date) => {
  selectedDate.value = date
  fetchCampusAttendanceStats()
}

watch([checkInData, checkOutData], () => {
  chartData.data.datasets[0].data = checkInData.value
  chartData.data.datasets[1].data = checkOutData.value
  daysInCurrentMonth.value = getDaysInMonth(selectedDate.value)
  chartData.data.labels = arrayRange(1, daysInCurrentMonth.value)
  chartData.tooltipHeader = getDatesForMonth(selectedDate.value)
})
watch([currentCampusScope, selectedCampus, selectedDate], () => {
  fetchCampusAttendanceStats()
})
onMounted(() => {
  setLastAttendanceCampus()
})
const CampusAttendanceStats = (payload) =>
  store
    .dispatch('instituteCount/getCampusAttendanceStats', payload)
    .then((response) => {
      if (response) {
        isSuccessCampusStats.value = true
      }
      return response
    })
    .catch((error) => {
      isSuccessCampusStats.value = false
    })

const lastMarkedCampusAttendance = () => {
  if (isSuccessCampusStats.value) store.dispatch('instituteCount/getLastMarkedCampusAttendance')
}
</script>
